import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Switch,
  TextInput
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import Header from '../../Components/Header';
import Button from '../../Components/Button';
import Input from '../../Components/Input';
import ScreenWrapper from '../../Components/ScreenWrapper';
import { theme } from '../../constants/theme';
import { useAuth } from '../../Utils/AuthProvider';
import { hp, wp } from '../../helpers/common';
import { createPoll } from '../../services/pollService';
import { fetchCommunityById } from '../../services/communityService';
import DateTimePicker from '@react-native-community/datetimepicker';
// import { Plus, X } from '../../assets/icons';

const CreatePoll = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { userData } = useAuth();
  
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultipleChoices, setAllowMultipleChoices] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [loading, setLoading] = useState(false);
  const [communityData, setCommunityData] = useState<any>(null);

  const { communityId } = (route.params as any) || {};

  useEffect(() => {
    if (communityId) {
      loadCommunityData();
    }
  }, [communityId]);

  const loadCommunityData = async () => {
    try {
      const result = await fetchCommunityById(communityId);
      if (result.success) {
        setCommunityData(result.data);
      }
    } catch (error) {
      console.error('Error loading community:', error);
    }
  };

  const addOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    } else {
      Alert.alert('Limit Reached', 'You can add maximum 10 options');
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    } else {
      Alert.alert('Minimum Options', 'Poll must have at least 2 options');
    }
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const validateForm = () => {
    if (!question.trim()) {
      Alert.alert('Error', 'Please enter a question');
      return false;
    }

    const validOptions = options.filter(option => option.trim());
    if (validOptions.length < 2) {
      Alert.alert('Error', 'Please enter at least 2 options');
      return false;
    }

    return true;
  };

  const handleCreatePoll = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const validOptions = options.filter(option => option.trim());
      
      const pollData = {
        question: question.trim(),
        description: description.trim(),
        options: validOptions,
        userId: userData.id,
        community_id: communityId,
        poll_type: isAnnouncement ? 'announcement' : 'regular',
        is_announcement: isAnnouncement,
        expires_at: expiresAt,
        allow_multiple_choices: allowMultipleChoices,
        is_anonymous: isAnonymous
      };

      const result = await createPoll(pollData);
      
      if (result.success) {
        Alert.alert(
          'Success',
          'Poll created successfully!',
          [
            {
              text: 'OK',
              onPress: () => {
                navigation.goBack();
                // Refresh the feed if there's a callback
                if ((route.params as any)?.onPollCreated) {
                  (route.params as any).onPollCreated(result.data);
                }
              }
            }
          ]
        );
      } else {
        Alert.alert('Error', result.msg);
      }
    } catch (error) {
      Alert.alert('Error', 'Could not create poll');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event: any, selectedDate?: Date) => {
    setShowDatePicker(false);
    if (selectedDate) {
      setExpiresAt(selectedDate);
    }
  };

  const formatDate = (date: Date | null) => {
    return date ? new Date(date).toLocaleDateString() : 'No expiry';
  };

  return (
    <ScreenWrapper>
      <Header
        title="Create Poll"
        showBackButton
      />
      
      {/* Create Button */}
      <View style={styles.headerActions}>
        <TouchableOpacity
          onPress={handleCreatePoll}
          disabled={loading}
          style={styles.createButton}
        >
          {loading ? (
            <ActivityIndicator size="small" color={theme.colors.primary} />
          ) : (
            <Text style={styles.createButtonText}>Create</Text>
          )}
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Community Info */}
        {communityData && (
          <View style={styles.communityInfo}>
            <Text style={styles.communityLabel}>Posting to:</Text>
            <Text style={styles.communityName}>{communityData.name}</Text>
          </View>
        )}

        {/* Question */}
        <View style={styles.section}>
          <Text style={styles.label}>Question *</Text>
          <Input
            value={question}
            onChangeText={setQuestion}
            placeholder="What would you like to ask?"
            placeholderTextColor="#9CA3AF"
            multiline
            style={styles.questionInput}
            maxLength={200}
          />
          <Text style={styles.characterCount}>{question.length}/200</Text>
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.label}>Description (Optional)</Text>
          <Input
            value={description}
            onChangeText={setDescription}
            placeholder="Add more details about your poll..."
            placeholderTextColor="#9CA3AF"
            multiline
            style={styles.descriptionInput}
            maxLength={500}
          />
          <Text style={styles.characterCount}>{description.length}/500</Text>
        </View>

        {/* Poll Options */}
        <View style={styles.section}>
          <Text style={styles.label}>Options *</Text>
          {options.map((option, index) => (
            <View key={index} style={styles.optionContainer}>
              <View style={styles.optionInputContainer}>
                <TextInput
                  value={option}
                  onChangeText={(value) => updateOption(index, value)}
                  placeholder={`Option ${index + 1}`}
                  placeholderTextColor="#9CA3AF"
                  selectionColor="#E91E63"
                  style={styles.optionInput}
                  maxLength={100}
                />
              </View>
              {options.length > 2 && (
                <TouchableOpacity
                  onPress={() => removeOption(index)}
                  style={styles.removeButton}
                >
                  <Text style={styles.removeIcon}>×</Text>
                </TouchableOpacity>
              )}
            </View>
          ))}
          
          {options.length < 10 && (
            <TouchableOpacity onPress={addOption} style={styles.addOptionButton}>
              <Text style={styles.addIcon}>+</Text>
              <Text style={styles.addOptionText}>Add Option</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Poll Settings */}
        <View style={styles.section}>
          <Text style={styles.label}>Poll Settings</Text>
          
          {/* Multiple Choice */}
          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Allow Multiple Choices</Text>
              <Text style={styles.settingDescription}>
                Users can select multiple options
              </Text>
            </View>
            <Switch
              value={allowMultipleChoices}
              onValueChange={setAllowMultipleChoices}
              trackColor={{ false: theme.colors.gray, true: theme.colors.primaryLight }}
              thumbColor={allowMultipleChoices ? theme.colors.primary : theme.colors.gray}
            />
          </View>

          {/* Anonymous Poll */}
          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Anonymous Poll</Text>
              <Text style={styles.settingDescription}>
                Hide voter identities
              </Text>
            </View>
            <Switch
              value={isAnonymous}
              onValueChange={setIsAnonymous}
              trackColor={{ false: theme.colors.gray, true: theme.colors.primaryLight }}
              thumbColor={isAnonymous ? theme.colors.primary : theme.colors.gray}
            />
          </View>

          {/* Expiry Date */}
          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Expiry Date</Text>
              <Text style={styles.settingDescription}>
                When should this poll expire?
              </Text>
            </View>
          <TouchableOpacity
            onPress={() => setShowDatePicker(true)}
            style={styles.dateButton}
          >
            <Text style={styles.dateButtonText}>{formatDate(expiresAt)}</Text>
          </TouchableOpacity>
          </View>

          {/* Announcement */}
          {communityData && (
            <View style={styles.settingRow}>
              <View style={styles.settingInfo}>
                <Text style={styles.settingTitle}>Post as Announcement</Text>
                <Text style={styles.settingDescription}>
                  Pin this poll as a community announcement
                </Text>
              </View>
              <Switch
                value={isAnnouncement}
                onValueChange={setIsAnnouncement}
                trackColor={{ false: theme.colors.gray, true: theme.colors.primaryLight }}
                thumbColor={isAnnouncement ? theme.colors.primary : theme.colors.gray}
              />
            </View>
          )}
        </View>
      </ScrollView>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <DateTimePicker
          value={expiresAt || new Date()}
          mode="date"
          display="default"
          minimumDate={new Date()}
          onChange={handleDateChange}
        />
      )}
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
  },
  headerActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: theme.colors.background,
  },
  createButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  createButtonText: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.primary,
  },
  communityInfo: {
    backgroundColor: theme.colors.gray,
    padding: 12,
    borderRadius: 8,
    marginVertical: 16,
  },
  communityLabel: {
    fontSize: hp(1.4),
    color: theme.colors.gray,
  },
  communityName: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.dark,
    marginTop: 4,
  },
  section: {
    marginBottom: 24,
  },
  label: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 8,
  },
  questionInput: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  descriptionInput: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  characterCount: {
    fontSize: hp(1.3),
    color: theme.colors.gray,
    textAlign: 'right',
    marginTop: 4,
  },
  optionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  optionInput: {
    flex: 1,
    color: theme.colors.text,
    fontSize: hp(1.6),
    backgroundColor: 'transparent',
    paddingVertical: 0,
    textAlignVertical: 'center',
  },
  optionInputContainer: {
    flex: 1,
    marginRight: 8,
    height: 60,
    borderWidth: 0.4,
    borderColor: theme.colors.grayDark,
    borderRadius: 8, // More rectangular border radius
    paddingHorizontal: 18,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
  },
  removeButton: {
    padding: 8,
  },
  removeIcon: {
    fontSize: 20,
    color: theme.colors.error,
    fontWeight: 'bold',
  },
  addIcon: {
    fontSize: 20,
    color: theme.colors.primary,
    fontWeight: 'bold',
  },
  addOptionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderStyle: 'dashed',
    borderRadius: 8,
    marginTop: 8,
  },
  addOptionText: {
    fontSize: hp(1.6),
    color: theme.colors.primary,
    marginLeft: 8,
    fontWeight: '500',
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray,
  },
  settingInfo: {
    flex: 1,
    marginRight: 16,
  },
  settingTitle: {
    fontSize: hp(1.6),
    fontWeight: '500',
    color: theme.colors.dark,
  },
  settingDescription: {
    fontSize: hp(1.4),
    color: theme.colors.gray,
    marginTop: 2,
  },
  dateButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: theme.colors.gray,
    borderRadius: 6,
  },
  dateButtonText: {
    fontSize: hp(1.5),
    color: theme.colors.dark,
  },
});

export default CreatePoll;
